import CustomInput from '../../reusable/input';
import CustomModal from '../../modals';
import { DuplicatePipelineParams } from '../../../params/pipeline';
import { duplicatePipelineSchema } from '../../../schema/pipeline_schema';
import { duplicatePipelineThunk } from '../../../redux/thunks/pipeline';
import { useParams } from 'react-router';
import { AppDispatch, RootState } from '../../../redux/store';
import { Box, Button } from '@mui/material';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

/**
 * @heading1: DuplicatePipeline
 *
 * @paragraph: A modal component for duplicating an existing pipeline.
 * It allows users to specify a new name for the duplicated pipeline and confirm the duplication.
 *
 * @param: {boolean} open - Determines whether the modal is open.
 * @param: {function} onClose - Function to handle closing the modal.
 */

const DuplicatePipeline = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
    /**
     * @heading3: Variables
     * @ol:
     * initialValues: {DuplicatePipelineParams} -  Initial form values based on the selected pipeline.
     * dispatch: {AppDispatch} - The Redux dispatch function to trigger actions.
     * loaders/selectedPipeline: An object containing the loader details from the Redux store.
     */
    const dispatch = useDispatch<AppDispatch>();
    const { id } = useParams<{ id: string }>();
    const { selectedPipeline, loaders } = useSelector((state: RootState) => state.pipeline);
    // Initial form values based on the selected pipeline
    const initialValues: DuplicatePipelineParams = {
        id: selectedPipeline?.id as number,
        pipeline_name: selectedPipeline?.name as string
    };

    /**
     * @heading3: Functions
     * @heading4: Handles the duplication of the pipeline.
     * @paragraph: Dispatches the duplicatePipelineThunk with the provided parameters.
     * @param: {DuplicatePipelineParams} values - The values entered in the form.
     */
    const duplicateHandler = async (values: DuplicatePipelineParams) => {
        try {
            await dispatch(duplicatePipelineThunk({
                data: {
                    id: values.id,
                    pipeline_name: values.pipeline_name
                },
                client_id: id ? parseInt(id as string, 10) : 0
            })).unwrap();
            // Close the modal after successful duplication
            onClose();
        } catch (e) {
            // Catch errors
        }
    };

    return <CustomModal open={open} handleClose={loaders.isLoading ? () => null : onClose} title="Delete Pipeline">
        <Formik
            initialValues={initialValues}
            validationSchema={duplicatePipelineSchema}
            onSubmit={duplicateHandler}
        >
            {({ values, handleChange, handleBlur, errors, touched }) => (
                <Form>
                    {/* Input field for the duplicate pipeline name */}
                    <CustomInput
                        value={values.pipeline_name}
                        onChange={handleChange('pipeline_name')}
                        onBlur={handleBlur('pipeline_name')}
                        error={touched.pipeline_name && errors.pipeline_name}
                        fullWidth
                        label="Duplicate Pipeline Name"
                        placeholder="Duplicate Pipeline Name"
                        variant="outlined"
                        disabled={loaders.isLoading}
                    />
                    {/* Action buttons */}
                    <Box display="flex" gap={2} mt={2}>
                        <Button variant="outlined" color="error" onClick={onClose} fullWidth disabled={loaders.isLoading}>
                            Cancel
                        </Button>
                        <Button type="submit" variant="contained" fullWidth disabled={loaders.isLoading}>
                            {loaders.isLoading ? 'Duplicating...' : 'Duplicate Pipeline'}
                        </Button>
                    </Box>
                </Form>
            )}
        </Formik>
    </CustomModal>;
};

export default DuplicatePipeline;